import { Alert, Button } from "antd";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import {
    BaseItem as ZhBaseItem,
    DetailHeader as ZhDetailHeader,
    Grid as ZhGrid,
    GridItem as ZhGridItem
} from "zhihao-ui-react";

import request from "@/api";
import { DATE_PICKER_FORMAT, DATETIME_PICKER_FORMAT, DEFAULT_VALUE } from "@/config";
import { INTEND_STATE, PURCHASE_TYPE } from "@/meta/purchase/intend";
import { areaLib } from "@/utils/areaLib";
import { getEnterpriseName } from "@/utils/customer";
import { dateFormat } from "@/utils";
import { formatNumber } from "@/utils/number";

export type IntendedDemandDetailData = Record<string, any>;

/** 复用 Vue 采购意向详情接口。 */
export async function loadIntendedDemandDetail(id: number | string): Promise<IntendedDemandDetailData> {
    const { data } = await request.service.get<IntendedDemandDetailData>(`/admin/purchase-demand/vo/${id}`);
    return data;
}

const display = (value: unknown) =>
    value === undefined || value === null || value === "" ? DEFAULT_VALUE : String(value);

/** 采购意向详情最小闭环。 */
export function IntendedDemandDetailPage({
    loadDetail = loadIntendedDemandDetail
}: {
    loadDetail?: (id: number | string) => Promise<IntendedDemandDetailData>;
}) {
    const { id = "" } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [detail, setDetail] = useState<IntendedDemandDetailData>();
    const [error, setError] = useState("");
    useEffect(() => {
        if (!id) return;
        void loadDetail(id)
            .then(setDetail)
            .catch(() => setError("采购意向详情加载失败"));
    }, [id, loadDetail]);
    const demand = detail?.purchaseDemand;
    return (
        <section aria-label="intended-demand-detail-page">
            <>
                <ZhDetailHeader
                    rightInfos={[
                        { label: "服务专员", value: display(detail?.user?.name) },
                        {
                            label: "创建时间",
                            value: demand?.createdTime
                                ? dateFormat(demand.createdTime, DATETIME_PICKER_FORMAT)
                                : DEFAULT_VALUE
                        }
                    ]}
                    stateDesc={INTEND_STATE.getIntroByCode(demand?.state) || DEFAULT_VALUE}
                    title="采购意向详情"
                />
                {error ? <Alert message={error} role="alert" type="error" /> : null}
                <ZhGrid cols={4} gap={[30, 20]}>
                    <ZhBaseItem label="编号" value={display(demand?.code || demand?.id)} />
                    <ZhBaseItem
                        label="状态"
                        value={INTEND_STATE.getIntroByCode(demand?.state) || display(demand?.state)}
                    />
                    <ZhBaseItem
                        label="采购类型"
                        value={PURCHASE_TYPE.getIntroByCode(demand?.purType) || display(demand?.purType)}
                    />
                    <ZhBaseItem label="联系人" value={display(demand?.contact)} />
                    <ZhBaseItem label="手机号" value={display(demand?.mobile)} />
                    <ZhBaseItem
                        label="收货区域"
                        value={
                            areaLib.getFullAreaName(demand?.provinceCode, demand?.cityCode, demand?.regionCode) ||
                            DEFAULT_VALUE
                        }
                    />
                    <ZhGridItem span={4}>
                        <ZhBaseItem
                            label="提货日期"
                            value={
                                demand?.deliveryTime
                                    ? dateFormat(demand.deliveryTime, DATE_PICKER_FORMAT)
                                    : DEFAULT_VALUE
                            }
                        />
                    </ZhGridItem>
                    <ZhBaseItem label="商品" value={display(demand?.productName)} />
                    <ZhBaseItem label="采购吨数" value={`${formatNumber(demand?.purQuantity)} 吨`} />
                    <ZhBaseItem label="备注" value={display(demand?.remark)} />
                    {/* 保持 Vue 详情页的供应商字段契约，统一兼容企业名称、实名和手机号。 */}
                    <ZhBaseItem label="供应商" value={getEnterpriseName(demand?.supplierEnterprise)} />
                    <ZhBaseItem label="项目" span={2}>
                        {detail?.project?.id ? (
                            <Link to={`/purchasingManage/project/info/${detail.project.id}`}>
                                {display(detail.project.name)}
                            </Link>
                        ) : (
                            DEFAULT_VALUE
                        )}
                    </ZhBaseItem>
                    <ZhBaseItem label="采购商" span={2}>
                        {demand?.buyerId ? (
                            <Link to={`/lianYunUser/userInfo?userId=${demand.buyerId}`}>
                                {display(demand.buyerEnterprise?.name)}
                            </Link>
                        ) : (
                            display(demand?.buyerEnterprise?.name)
                        )}
                    </ZhBaseItem>
                </ZhGrid>
                <Button className="mt-6" onClick={() => void navigate("/purchasingManage/intendedDemand")}>
                    返回
                </Button>
            </>
        </section>
    );
}

export default IntendedDemandDetailPage;
